import React from "react";
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell, PieChart, Pie, RadialBarChart, RadialBar } from "recharts";
import { fmtDate } from "../lib/time";

const shortDay = (d) => (d && d.length >= 10 ? `${d.slice(8, 10)}/${d.slice(5, 7)}` : d || "");

export function RadialGauge({ value, max = 100, color = "#4338CA", suffix = "", label, height = 200, testId }) {
  const v = value == null ? 0 : value;
  const data = [{ name: "v", value: Math.max(0, Math.min(max, v)), fill: color }];
  return (
    <div data-testid={testId} className="relative" style={{ width: "100%", height }}>
      <ResponsiveContainer>
        <RadialBarChart innerRadius="70%" outerRadius="100%" data={data} startAngle={90} endAngle={-270}>
          <defs />
          <RadialBar background={{ fill: "#EEF2F7" }} dataKey="value" cornerRadius={20} max={max} />
        </RadialBarChart>
      </ResponsiveContainer>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <span className="sv-metric text-4xl font-bold leading-none" style={{ color }}>
          {value == null ? "—" : value}{suffix}
        </span>
        {label && <span className="mt-1 text-xs font-medium text-slate-400">{label}</span>}
      </div>
    </div>
  );
}

export function Donut({ data, centerValue, centerLabel, height = 200, onSlice, activeName, testId }) {
  const items = (data || []).filter((d) => d.value > 0);
  const empty = items.length === 0;
  const clickable = typeof onSlice === "function";
  const handle = (name) => { if (clickable) onSlice(activeName === name ? null : name); };
  return (
    <div data-testid={testId}>
      <div className="relative" style={{ width: "100%", height }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie data={empty ? [{ name: "none", value: 1 }] : items} dataKey="value" nameKey="name"
              cx="50%" cy="50%" innerRadius="66%" outerRadius="92%" paddingAngle={empty ? 0 : 3} stroke="none"
              onClick={(_, i) => !empty && handle(items[i].name)} style={{ cursor: clickable && !empty ? "pointer" : "default" }}>
              {(empty ? [{ color: "#EEF2F7" }] : items).map((d, i) => (
                <Cell key={i} fill={d.color} opacity={activeName && activeName !== d.name ? 0.3 : 1} />
              ))}
            </Pie>
            {!empty && <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />}
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="sv-metric text-3xl font-bold leading-none text-slate-800">{centerValue}</span>
          {centerLabel && <span className="mt-1 text-xs font-medium text-slate-400">{centerLabel}</span>}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
        {(data || []).map((d) => {
          const active = activeName === d.name;
          const Tag = clickable ? "button" : "span";
          return (
            <Tag key={d.name} type={clickable ? "button" : undefined}
              onClick={clickable ? () => handle(d.name) : undefined}
              data-testid={clickable ? `${testId}-legend-${d.name.toLowerCase().replace(/\s+/g, "-")}` : undefined}
              className={`flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium transition-colors ${clickable ? "hover:bg-slate-100" : ""} ${active ? "bg-slate-100 text-slate-900" : "text-slate-500"}`}>
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: d.color }} />
              {d.name} <span className="font-bold text-slate-700">{d.value}</span>
            </Tag>
          );
        })}
      </div>
    </div>
  );
}

export function TrendArea({ data, height = 220, color = "#4338CA", dataKey = "value", testId }) {
  if (!data || data.length === 0) {
    return <div className="flex h-[180px] items-center justify-center text-sm text-slate-400">Not enough data for a trend yet.</div>;
  }
  return (
    <div data-testid={testId} style={{ width: "100%", height }}>
      <ResponsiveContainer>
        <AreaChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
          <defs>
            <linearGradient id={`g-${testId}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.25} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
          <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94A3B8" }} tickFormatter={shortDay} minTickGap={24} />
          <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#94A3B8" }} width={40} />
          <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }}
            labelFormatter={(d) => `Date ${fmtDate(d)}`} formatter={(v) => [`${v}`, "Index"]} />
          <Area type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2.5} fill={`url(#g-${testId})`} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function BarSeries({ data, height = 240, color = "#4338CA", xKey = "label", yKey = "count", highlightKey, testId }) {
  if (!data || data.length === 0) {
    return <div className="flex h-[180px] items-center justify-center text-sm text-slate-400">Not enough data yet.</div>;
  }
  return (
    <div data-testid={testId} style={{ width: "100%", height }}>
      <ResponsiveContainer>
        <BarChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
          <XAxis dataKey={xKey} tick={{ fontSize: 10, fill: "#94A3B8" }} interval={0} angle={data.length > 10 ? -35 : 0} textAnchor={data.length > 10 ? "end" : "middle"} height={data.length > 10 ? 50 : 20} />
          <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} width={40} allowDecimals={false} />
          <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
          <Bar dataKey={yKey} radius={[4, 4, 0, 0]}>
            {data.map((d, i) => (
              <Cell key={i} fill={highlightKey && d[highlightKey] ? "#10B981" : color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}


export function VolumeArea({ data, height = 200, color = "#4338CA", label = "Count", testId }) {
  const empty = !data || data.length === 0 || data.every((d) => !d.count);
  if (empty) {
    return <div data-testid={testId} className="flex h-[160px] items-center justify-center text-sm text-slate-400">No activity in this period yet.</div>;
  }
  return (
    <div data-testid={testId} style={{ width: "100%", height }}>
      <ResponsiveContainer minWidth={100} minHeight={120}>
        <AreaChart data={data} margin={{ top: 10, right: 10, left: -24, bottom: 0 }}>
          <defs>
            <linearGradient id={`va-${testId}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.3} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
          <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94A3B8" }} tickFormatter={shortDay} minTickGap={24} />
          <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} width={44} allowDecimals={false} />
          <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }}
            labelFormatter={(d) => `Date ${fmtDate(d)}`} formatter={(v) => [`${v}`, label]} />
          <Area type="monotone" dataKey="count" stroke={color} strokeWidth={2.5} fill={`url(#va-${testId})`} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
